'use client';

import { useAuth } from '@/backend/context/auth-management';
import { useOwner } from '@/backend/firebase/hooks/useOwner';
import { FormGuard } from '@/components/form-guard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  PAYMENT_PRESETS,
  getFieldHelperText,
  getPresetByTitle,
  type PaymentPreset,
} from '@/config/payment-presets';
import {
  Currencies,
  type CurrenciesType,
  type FinanceData,
  type PaymentMethod,
} from '@/shared/schema';
import { cn } from '@/lib/utils';
import { generateUUID } from '@/shared/utils';
import {
  AlertCircle,
  Banknote,
  Bitcoin,
  Check,
  Coins,
  CreditCard,
  DollarSign,
  Landmark,
  Plus,
  Smartphone,
  Sparkles,
  Trash2,
  Wallet,
  Zap,
} from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { useEffect } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';

// Icon mapping
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Smartphone,
  DollarSign,
  Zap,
  Landmark,
  Bitcoin,
  CreditCard,
  Banknote,
};

const getIconComponent = (iconName: string) => {
  return iconMap[iconName] || Banknote;
};

export default function FinancePage() {
  const profile = useAuth((s) => s.profile);
  const refreshProfile = useAuth((s) => s.refreshProfile);
  const updatePublicMutation = useOwner.updatePublic();

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isDirty, isSubmitting, isValid },
  } = useForm<FinanceData>({
    defaultValues: {
      paymentMethods: [],
      currencyConfig: {
        accountingCurrency: profile?.currencyConfig.accountingCurrency,
        localCurrency: profile?.currencyConfig.localCurrency,
        multiCurrency: true,
        ratePolicy: 'official',
      },
    },
  });

  const currencyConfig = useWatch({
    control,
    name: 'currencyConfig',
  });

  // Sync with Firebase data
  useEffect(() => {
    if (profile) {
      const finance = profile.public?.finance;

      // Priorizamos la configuración del perfil raíz si la de finanzas públicas
      // parece ser la por defecto ('BS') y el perfil tiene algo distinto (como 'COP')
      const initialLocal =
        finance?.currencyConfig?.localCurrency === 'BS' &&
        profile.currencyConfig?.localCurrency !== 'BS'
          ? profile.currencyConfig.localCurrency
          : finance?.currencyConfig?.localCurrency ||
            profile.currencyConfig?.localCurrency ||
            'BS';

      const initialAccounting =
        finance?.currencyConfig?.accountingCurrency === 'USD' &&
        profile.currencyConfig?.accountingCurrency !== 'USD'
          ? profile.currencyConfig.accountingCurrency
          : finance?.currencyConfig?.accountingCurrency ||
            profile.currencyConfig?.accountingCurrency ||
            'USD';

      reset({
        paymentMethods: finance?.paymentMethods || [],
        currencyConfig: {
          accountingCurrency: initialAccounting as CurrenciesType,
          localCurrency: initialLocal as CurrenciesType,
          multiCurrency:
            finance?.currencyConfig?.multiCurrency ??
            profile.currencyConfig?.multiCurrency ??
            true,
          ratePolicy:
            finance?.currencyConfig?.ratePolicy ||
            profile.currencyConfig?.ratePolicy ||
            'official',
        },
      });
    }
  }, [profile, reset]);

  const {
    fields: methods,
    append: appendMethod,
    remove: removeMethod,
  } = useFieldArray({
    control,
    name: 'paymentMethods',
  });

  const onSubmit = async (data: FinanceData) => {
    try {
      // If multiCurrency is false, force all methods to use the local currency
      const finalizedData = {
        ...data,
        paymentMethods: data.paymentMethods.map((m) => ({
          ...m,
          currency: data.currencyConfig.multiCurrency
            ? m.currency
            : data.currencyConfig.localCurrency,
        })),
      };

      await updatePublicMutation.mutateAsync({ finance: finalizedData });
      await refreshProfile();
      reset(finalizedData);
      toast.success('Cambios guardados correctamente');
    } catch (error) {
      toast.error('Error al guardar los cambios');
    }
  };

  const addPresetMethod = (preset: PaymentPreset) => {
    const id = generateUUID();
    appendMethod({
      id: id,
      title: preset.label,
      currency: preset.currency,
      type: preset.type,
      isActive: true,
      fields: preset.fields.map((f) => ({
        name: f.name,
        value: '',
      })),
      isNew: true, // Internal flag to handle auto-focus
    } as any, { shouldFocus: false });

    toast.success(`${preset.label} listo. Escribe los datos aquí abajo.`);
    
    // Scroll to the new method
    setTimeout(() => {
      const element = document.getElementById(`method-${id}`);
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const addCustomMethod = () => {
    appendMethod({
      id: generateUUID(),
      title: 'Nuevo Método',
      currency: currencyConfig.localCurrency || 'USD',
      type: 'other',
      isActive: true,
      fields: [{ name: 'Detalle', value: '' }],
    } as PaymentMethod, { shouldFocus: false });
  };

  return (
    <div className="bg-muted/20 min-h-screen pb-32">
      <main className="mx-auto max-w-md space-y-6 p-4">
        <PageHeader 
          title="Cobros"
          subtitle="Mis cuentas y bancos"
          showBack={true}
        />
        {/* --- Payment Methods Section --- */}
        <div className="space-y-4">
          <div id="methods-list" className="flex items-center justify-between px-1">
            <div className="flex items-center gap-2">
              <Coins className="h-4 w-4 text-zinc-400" />
              <h2 className="text-zinc-900 text-sm font-black tracking-widest uppercase italic">
                Cuentas activas
              </h2>
            </div>
            <Badge variant="outline" className="bg-white border-zinc-200 text-zinc-500 font-bold">
              {methods.length} {methods.length === 1 ? 'Método' : 'Métodos'}
            </Badge>
          </div>

          {/* Preset Selection Grid */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 px-1">
              <Sparkles className="h-4 w-4 text-amber-500" />
              <h3 className="text-foreground text-xs font-black tracking-widest uppercase">
                Añadir Rápido
              </h3>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {PAYMENT_PRESETS.filter((p) => 
                !p.supportedCurrencies || 
                p.supportedCurrencies.includes(currencyConfig.localCurrency as any) ||
                p.currency === 'USD'
              ).map((preset) => {
                const IconComponent = getIconComponent(preset.icon);
                const isPagoMovil = preset.id === 'pago-movil';
                return (
                  <Button
                    key={preset.id}
                    variant="outline"
                    type="button"
                    onClick={() => addPresetMethod(preset)}
                    className={cn(
                      "h-auto flex-col gap-1.5 rounded-2xl p-3 transition-all active:scale-95",
                      isPagoMovil 
                        ? "border-zinc-900 bg-zinc-900 text-white hover:bg-zinc-800" 
                        : "border-zinc-200 bg-white hover:border-zinc-400"
                    )}
                  >
                    <div className={cn(
                      "flex h-8 w-8 items-center justify-center rounded-lg",
                      isPagoMovil ? "bg-white/10" : "bg-zinc-100"
                    )}>
                      <IconComponent className={cn("h-4 w-4", isPagoMovil ? "text-white" : "text-zinc-600")} />
                    </div>
                    <span className={cn("text-[10px] font-black uppercase tracking-tight", isPagoMovil ? "text-white" : "text-zinc-700")}>
                      {preset.label}
                    </span>
                    <Badge variant="outline" className={cn(
                      "px-1.5 py-0 text-[8px] font-black",
                      isPagoMovil ? "bg-white/20 border-white/30 text-white" : "bg-zinc-50 border-zinc-100 text-zinc-400"
                    )}>
                      {preset.currency}
                    </Badge>
                  </Button>
                );
              })}
            </div>
          </div>

          {/* Divider */}
          <div className="relative py-2">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="bg-muted/20 px-3 text-[10px] font-bold text-slate-400 uppercase">
                O personaliza
              </span>
            </div>
          </div>

          {/* Custom Method Button */}
          <Button
            variant="ghost"
            type="button"
            onClick={addCustomMethod}
            className="h-10 w-full rounded-xl border border-dashed border-zinc-200 bg-transparent text-[10px] font-black uppercase tracking-widest text-zinc-400 opacity-60 hover:opacity-100"
          >
            <Plus className="mr-2 h-3 w-3" />
            Crear otro método
          </Button>

          {/* Payment Method Cards */}
          <div className="space-y-4 pt-2">
            {methods.map((method, methodIndex) => (
              <PaymentMethodCard
                key={method.id}
                methodIndex={methodIndex}
                control={control}
                register={register}
                removeMethod={() => removeMethod(methodIndex)}
                currencyConfig={currencyConfig}
              />
            ))}
          </div>

          {/* Empty State */}
          {methods.length === 0 && (
            <Card className="border-dashed border-slate-300 bg-slate-50/50">
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
                  <Wallet className="h-8 w-8 text-slate-400" />
                </div>
                <h3 className="mb-2 text-sm font-bold text-slate-700">
                  No tienes métodos de pago configurados
                </h3>
                <p className="mb-6 max-w-xs text-xs text-slate-500">
                  Añade al menos un método de pago para que tus clientes puedan
                  pagarte. Usa los presets de arriba para configurar en
                  segundos.
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addPresetMethod(PAYMENT_PRESETS[0])}
                    className="rounded-xl"
                  >
                    <Smartphone className="mr-2 h-4 w-4" />
                    Añadir {currencyConfig.localCurrency === 'BS' ? 'Pago Móvil' : 'Método Regional'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      <FormGuard
        isDirty={isDirty}
        isValid={isValid}
        isSubmitting={isSubmitting}
        onSave={handleSubmit(onSubmit)}
      />
    </div>
  );
}

// Subcomponente PaymentMethodCard
function PaymentMethodCard({
  methodIndex,
  control,
  register,
  removeMethod,
  currencyConfig,
}: {
  methodIndex: number;
  control: any;
  register: any;
  removeMethod: () => void;
  currencyConfig: any;
}) {
  const {
    fields,
    append: appendField,
    remove: removeField,
  } = useFieldArray({
    control,
    name: `paymentMethods.${methodIndex}.fields`,
  });

  const isMulti = currencyConfig?.multiCurrency ?? true;
  const localCurrency = currencyConfig?.localCurrency || 'USD';

  // Get the method title to determine if it's a preset
  const methodTitle =
    control._formValues?.paymentMethods?.[methodIndex]?.title || '';
  const preset = getPresetByTitle(methodTitle);
  const IconComponent = preset ? getIconComponent(preset.icon) : Banknote;

  // Check if method is complete (all required fields filled)
  const isMethodComplete = () => {
    if (!preset) return true; // Custom methods are always considered complete
    const methodFields =
      control._formValues?.paymentMethods?.[methodIndex]?.fields || [];
    return preset.fields.every((fieldConfig, idx) => {
      if (!fieldConfig.required) return true;
      const value = methodFields[idx]?.value || '';
      return value.trim() !== '';
    });
  };

  const complete = isMethodComplete();

  return (
    <Card id={`method-${control._formValues?.paymentMethods?.[methodIndex]?.id}`} className={cn(
      "border-muted pt-0 overflow-hidden rounded-[2.5rem] shadow-xl transition-all",
      !complete && "ring-2  ring-amber-500/20 border-amber-200"
    )}>
      <div className="bg-zinc-900 border-zinc-800 flex items-center justify-between border-b p-5">
        <div className="flex flex-1 items-center gap-3">
          <Controller
            control={control}
            name={`paymentMethods.${methodIndex}.isActive`}
            render={({ field }) => (
              <Switch
                checked={field.value}
                onCheckedChange={field.onChange}
                className="shrink-0 data-[state=checked]:bg-emerald-500"
              />
            )}
          />

          {/* Method Icon */}
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100">
            <IconComponent className="h-4 w-4 text-slate-600" />
          </div>

          <Input
            {...register(`paymentMethods.${methodIndex}.title`)}
            className="h-8 w-full border-none bg-transparent px-0 text-sm font-black uppercase text-white focus-visible:ring-0 italic tracking-tighter"
            placeholder="Nombre del método"
          />
        </div>

        <div className="flex items-center gap-2">
          {/* Completion Badge */}
          {preset &&
            (complete ? (
              <Badge className="border-green-200 bg-green-100 text-[9px] text-green-700">
                <Check className="mr-1 h-2.5 w-2.5" />
                Listo
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="border-amber-200 bg-amber-50 text-[9px] text-amber-700"
              >
                <AlertCircle className="mr-1 h-2.5 w-2.5" />
                Incompleto
              </Badge>
            ))}

          <Button
            variant="ghost"
            size="icon"
            onClick={removeMethod}
            className="text-destructive hover:bg-destructive/10 h-8 w-8"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <CardContent className="space-y-4 bg-white p-4">
        {isMulti ? (
          <Controller
            control={control}
            name={`paymentMethods.${methodIndex}.currency`}
            render={({ field }) => (
              <div className="scrollbar-hide flex gap-1 overflow-x-auto rounded-xl bg-slate-100 p-1">
                {Currencies.options
                  .filter((curr) => 
                    // Si el local es BS, permitimos el trío multi-currency
                    (localCurrency === 'BS' && ['USD', 'EUR', 'BS'].includes(curr)) ||
                    // Si no es BS, permitimos el local + USD
                    (localCurrency !== 'BS' && [localCurrency, 'USD'].includes(curr)) ||
                    // Siempre permitimos el actual seleccionado para no romperlo
                    field.value === curr
                  )
                  .map((curr) => (
                    <button
                      key={curr}
                      type="button"
                      onClick={() => field.onChange(curr)}
                      className={`min-w-[60px] flex-1 rounded-lg py-2 text-[10px] font-black tracking-widest uppercase transition-all ${
                        field.value === curr
                          ? 'bg-white text-teal-600 shadow-sm'
                          : 'text-slate-400 hover:text-slate-600'
                      }`}
                    >
                      {curr}
                    </button>
                  ))}
              </div>
            )}
          />
        ) : (
          <div className="flex items-center justify-between rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-4 py-2.5">
            <span className="text-[10px] font-black tracking-widest text-slate-400 uppercase">
              Moneda Fija (Local)
            </span>
            <Badge
              variant="outline"
              className="border-teal-100 bg-teal-50 font-black text-teal-700"
            >
              {localCurrency}
            </Badge>
          </div>
        )}

        {/* Smart Field Inputs */}
        <div className="space-y-3">
          {fields.map((field, fieldIndex) => {
            const fieldConfig = preset?.fields[fieldIndex];
            const fieldName =
              control._formValues?.paymentMethods?.[methodIndex]?.fields?.[
                fieldIndex
              ]?.name || 'Detalle';

            return (
              <div key={field.id} className="space-y-1.5">
                {/* Clear Label */}
                <Label className="ml-1 text-[10px] font-black text-slate-500 uppercase">
                  {fieldConfig?.name || fieldName}
                  {fieldConfig?.required && (
                    <span className="ml-0.5 text-red-400">*</span>
                  )}
                </Label>

                {/* Contextual Input */}
                <div className="relative">
                  <Input
                    {...register(
                      `paymentMethods.${methodIndex}.fields.${fieldIndex}.value`,
                    )}
                    type={fieldConfig?.inputType || 'text'}
                    autoFocus={false}
                    className="h-14 rounded-2xl border-none bg-zinc-50 pr-10 text-sm font-black shadow-inner transition-all focus:bg-white focus:ring-2 focus:ring-zinc-900"
                    placeholder={fieldConfig?.placeholder || 'Ingresa el valor'}
                  />

                  {/* Delete Field Button (only for custom methods) */}
                  {!preset && (
                    <button
                      type="button"
                      onClick={() => removeField(fieldIndex)}
                      className="text-muted-foreground/40 active:bg-destructive/10 active:text-destructive absolute top-1/2 right-2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>

                {/* Helper Text */}
                {fieldConfig && (
                  <p className="ml-1 text-[9px] text-slate-400">
                    {getFieldHelperText(fieldConfig.name)}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Add Field Button (only for custom methods) */}
        {!preset && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => appendField({ name: 'Detalle', value: '' })}
            className="mt-2 h-9 w-full rounded-xl border border-dashed border-teal-200 bg-teal-50 text-[10px] font-bold tracking-widest text-teal-600 uppercase transition-all hover:bg-teal-100"
          >
            <Plus className="mr-1 h-3 w-3" />
            Añadir Campo
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
